import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { LogIn, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Beim Öffnen: gespeicherte Anmeldedaten vorbelegen
  const savedEmail = (() => { try { return localStorage.getItem('farbwelt_remember_email') || ''; } catch(_) { return ''; } })();
  const savedPassword = (() => { try { return atob(localStorage.getItem('farbwelt_remember_pw') || '') || ''; } catch(_) { return ''; } })();

  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState(savedPassword);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Anmeldedaten merken / vergessen je nach Toggle
        try {
          if (rememberMe) {
            localStorage.setItem('farbwelt_remember_email', email);
            localStorage.setItem('farbwelt_remember_pw', btoa(password));
          } else {
            localStorage.removeItem('farbwelt_remember_email');
            localStorage.removeItem('farbwelt_remember_pw');
          }
        } catch (_) {}
        toast.success(`Willkommen zurück, ${result.user.first_name}!`);
        
        // Redirect based on role
        if (result.user.role === 'admin') {
          navigate('/admin');
        } else if (result.user.role === 'employee') {
          navigate('/stempeluhr');
        } else {
          navigate(from, { replace: true });
        }
      } else {
        toast.error(result.error || 'Anmeldung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);

    try {
      const response = await fetch(`/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: forgotEmail })
      });
      
      if (!response.ok) {
        throw new Error('Request failed');
      }
      
      toast.success(
        <div>
          <p className="font-bold">Neues Passwort gesendet!</p>
          <p className="mt-1 text-sm">Falls ein Konto mit dieser E-Mail existiert, erhalten Sie Ihr neues Passwort per E-Mail.</p>
          <p className="text-xs mt-2 text-gray-500">Prüfen Sie auch Ihren Spam-Ordner.</p>
        </div>,
        { duration: 8000 }
      );
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('Fehler beim Senden der E-Mail');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center py-12 px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <picture>
              <source srcSet="/logo-new.webp" type="image/webp" />
              <img 
                src="/logo-new.png" 
                alt="FarbWelt Maler Express Service Logo" 
                className="h-20 w-auto object-contain"
                fetchPriority="high"
                decoding="sync"
              />
            </picture>
          </Link>
        </div>

        {!showForgotPassword ? (
          <Card className="shadow-lg" data-testid="login-card">
            <CardHeader className="text-center pb-2">
              <div className="w-14 h-14 bg-[#9B1B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-7 h-7 text-[#9B1B30]" />
              </div>
              <CardTitle className="font-barlow text-2xl">Anmelden</CardTitle>
              <CardDescription className="font-manrope">
                Melden Sie sich in Ihrem Kundenkonto an
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="font-barlow font-bold text-sm">E-Mail</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      placeholder="ihre@email.de"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="font-barlow font-bold text-sm">Passwort</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      placeholder="••••••••"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none" data-testid="remember-me-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 accent-[#9B1B30] cursor-pointer"
                      data-testid="remember-me-checkbox"
                    />
                    Angemeldet bleiben
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#9B1B30] hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Passwort vergessen?
                  </button>
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full mt-6"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Wird angemeldet...' : 'Anmelden'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="font-manrope text-sm text-gray-600">
                  Noch kein Konto?{' '}
                  <Link to="/register" className="text-[#9B1B30] font-medium hover:underline">
                    Jetzt registrieren
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg" data-testid="forgot-password-card">
            <CardHeader className="text-center pb-2">
              <div className="w-14 h-14 bg-[#9B1B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-[#9B1B30]" />
              </div>
              <CardTitle className="font-barlow text-2xl">Passwort vergessen?</CardTitle>
              <CardDescription className="font-manrope">
                Geben Sie Ihre E-Mail-Adresse ein, um ein neues Passwort zu erhalten.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email" className="font-barlow font-bold text-sm">E-Mail</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="pl-10"
                      placeholder="ihre@email.de"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full mt-6"
                  disabled={forgotLoading}
                  data-testid="forgot-password-submit"
                >
                  {forgotLoading ? 'Wird verarbeitet...' : 'Neues Passwort anfordern'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="text-sm text-[#9B1B30] hover:underline font-medium"
                >
                  ← Zurück zur Anmeldung
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center mt-6 font-manrope text-sm text-gray-500">
          <Link to="/lizenz-registrieren" className="text-[#9B1B30] hover:underline">Mit Lizenzschlüssel registrieren</Link>
          <span className="mx-2">|</span>
          <Link to="/" className="hover:text-[#9B1B30]">Zurück zur Startseite</Link>
        </p>
      </div>
    </div>
  );
}
